// TEMPORARY MOCK ADAPTER for Health Encyclopedia (PRD 10).
// In-memory only — state lives in this module's `categories` + `contents`
// arrays and resets on hard reload. Mutations resolve with ~300 ms simulated
// latency. Errors mimic the backend envelope (HealthEncyclopediaApiError).
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter
// (health-encyclopedia.adapter.ts). Contract: docs/api-contracts/health-encyclopedia.md.
//
// Seeded from prototype/js/mock-data.js (healthEncyclopediaCategories ×6,
// healthEncyclopedia ×10). The list endpoint simulates server-side
// filter/search/sort/pagination so the React Query hooks can be written
// against the real (server-side) contract from day one.

import { withoutEmptyMediaBlocks } from '@/shared/utils/block-validation';

import {
  HealthEncyclopediaApiError,
  MAX_HOME_EXPOSED_FALLBACK,
  type Category,
  type CategoryListResult,
  type CategoryNameInput,
  type ContentBlock,
  type ContentDetail,
  type ContentListParams,
  type ContentListResult,
  type ContentStatus,
  type ContentSummary,
  type CreateContentInput,
  type HealthEncyclopediaApi,
  type UpdateContentInput,
  type UploadResult,
} from '../types/health-encyclopedia.types';

const LATENCY_MS = 300;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ── Internal record shapes ───────────────────────────────────────────────────
// Categories keep the prototype's `{ id, name, order }`; `contentCount` is
// derived on read. Contents keep a numeric `id` (drives EID + sort) plus the
// full block array; the EID `E-###` is derived from `id`.
interface CategoryRecord {
  id: number;
  name: string;
  order: number;
}

interface ContentRecord {
  id: number;
  title: string;
  categoryId: number | null;
  thumbnailUrl: string | null;
  preview: string;
  status: ContentStatus;
  publishedAt: string | null;
  homeExposed: boolean;
  blocks: ContentBlock[];
}

// ── Seed: categories (prototype MockData.healthEncyclopediaCategories) ───────
let categories: CategoryRecord[] = [
  { id: 1, name: '우울감', order: 1 },
  { id: 2, name: '수면·피로', order: 2 },
  { id: 3, name: '신체활동', order: 3 },
  { id: 4, name: '가족·돌봄', order: 4 },
  { id: 5, name: '질병 이해', order: 5 },
  { id: 6, name: '의료자원', order: 6 },
];

// ── Seed: contents (prototype MockData.healthEncyclopedia) ───────────────────
// Blocks are derived from `preview` on first build (prototype normalizeDataModel):
// a single text block carrying the preview text. Thumbnails are null in seed.
interface SeedContent {
  id: number;
  title: string;
  categoryId: number | null;
  status: ContentStatus;
  publishedAt: string | null;
  preview: string;
}

const seed: SeedContent[] = [
  { id: 55, title: '응급 상황 시 연락처와 대처 방법', categoryId: 6, status: 'PUBLISHED', publishedAt: '2026.03.18', preview: '위기 상황에서는 혼자 견디지 마세요. 자살예방상담전화(109)·정신건강상담전화(1577-0199) 등 즉시 도움받을 수 있는 연락처와...' },
  { id: 54, title: '정신건강 위기 상담 지원 기관 안내', categoryId: 6, status: 'PUBLISHED', publishedAt: '2026.03.17', preview: '전국 정신건강복지센터, 지역별 상담 기관, 24시간 위기 대응 창구를 한눈에 정리했습니다. 가까운 기관을 찾아...' },
  { id: 53, title: '내가 지금 우울한가 — 우울 자가 점검', categoryId: 1, status: 'DRAFT', publishedAt: null, preview: '최근 2주간 나의 기분과 일상을 돌아보며 우울감의 정도를 스스로 점검해볼 수 있는 간단한 자가 체크리스트입니다...' },
  { id: 52, title: '우울증상 경고 신호 알아두기', categoryId: 1, status: 'PUBLISHED', publishedAt: '2026.03.16', preview: '지속되는 무기력, 흥미 상실, 수면·식욕 변화 등 우울증의 주요 경고 신호를 미리 알아두면 빠르게 대처할 수 있습니다...' },
  { id: 51, title: '우울한 기분이 있을 때 도움이 되는 방법', categoryId: 1, status: 'PUBLISHED', publishedAt: '2026.03.15', preview: '우울한 기분이 들 때 일상에서 시도해볼 수 있는 작은 실천들을 소개합니다. 햇볕 쬐기, 가벼운 산책, 감정 기록하기 등...' },
  { id: 50, title: '규칙적인 수면 습관 만들기', categoryId: 2, status: 'PUBLISHED', publishedAt: '2026.03.14', preview: '규칙적인 수면은 정신 건강에 매우 중요합니다. 매일 같은 시간에 잠자리에 들고 일어나는 습관을 들이면...' },
  { id: 49, title: '조현병의 이해와 관리', categoryId: 5, status: 'PUBLISHED', publishedAt: '2026.03.12', preview: '조현병은 사고, 감정, 행동에 영향을 미치는 만성 정신 질환입니다. 적절한 치료와 지원으로 일상생활이...' },
  { id: 48, title: '가벼운 스트레칭 재활 운동', categoryId: 3, status: 'PUBLISHED', publishedAt: '2026.03.10', preview: '재활 운동은 신체 기능 회복과 정신 건강 향상에 도움이 됩니다. 매일 10분씩 가벼운 스트레칭으로...' },
  { id: 47, title: '가족이 알아야 할 정신건강 지원법', categoryId: 4, status: 'PUBLISHED', publishedAt: '2026.03.08', preview: '가족의 지지는 환자의 회복에 큰 영향을 미칩니다. 올바른 소통 방법과 지원 방식을 알아보세요...' },
  { id: 46, title: '우울증 극복을 위한 일상 관리', categoryId: 1, status: 'DRAFT', publishedAt: null, preview: '우울증은 의지의 문제가 아닌 치료가 필요한 질환입니다. 일상에서 실천할 수 있는 관리 방법을...' },
];

let contents: ContentRecord[] = seed.map((row) => ({
  id: row.id,
  title: row.title,
  categoryId: row.categoryId,
  thumbnailUrl: null,
  preview: row.preview,
  status: row.status,
  publishedAt: row.publishedAt,
  // PRD §4.2 — home exposure defaults OFF for every seeded content, matching the
  // prototype's normalizeDataModel and the BE column default.
  homeExposed: false,
  // Derive a single text block from the preview, mirroring the prototype's
  // normalizeDataModel. The trailing "..." ellipsis is stripped.
  blocks: row.preview
    ? [{ id: 1, type: 'text', text: row.preview.replace(/\.\.\.$/, '').trim() }]
    : [],
}));

// ── Helpers ──────────────────────────────────────────────────────────────────
const MAX_TITLE = 100;
const MAX_CATEGORY_NAME = 20;
// Clarification ADM.015 / ADM.018 — block-editor limits (mirrors BE BlockContentValidator).
const MAX_BLOCKS = 10;
const MAX_TEXT_BLOCK = 500;
const VIDEO_TITLE_MAX = 50;
const VIDEO_HOSTS = ['youtube.com', 'youtu.be', 'vimeo.com'];
const isVideoUrlValid = (url: string): boolean => {
  let u: URL;
  try {
    u = new URL(url.trim());
  } catch {
    return false;
  }
  if (u.protocol !== 'https:') return false;
  const host = u.hostname.toLowerCase();
  return VIDEO_HOSTS.some((base) => host === base || host.endsWith(`.${base}`));
};
// ADM.018 — optional: absent/empty is valid, but a whitespace-only title is NOT (it renders as
// an empty caption while looking filled-in). Kept standalone rather than importing the FE
// validator: this file stands in for the server, and the rule mirrors
// BlockContentValidator.validateVideoTitle.
const isVideoTitleValid = (title: string | undefined): boolean => {
  if (title == null || title === '') return true;
  const trimmed = title.trim();
  return trimmed.length > 0 && trimmed.length <= VIDEO_TITLE_MAX;
};

const toEid = (id: number): string => `E-${String(id).padStart(3, '0')}`;
const fromEid = (eid: string): number => Number.parseInt(eid.replace(/^E-/, ''), 10);

const categoryById = (id: number | null): CategoryRecord | null =>
  id == null ? null : categories.find((c) => c.id === id) ?? null;

const categoryName = (id: number | null): string | null => categoryById(id)?.name ?? null;

const countByCategory = (id: number): number =>
  contents.filter((c) => c.categoryId === id).length;

const sortedCategories = (): CategoryRecord[] =>
  [...categories].sort((a, b) => a.order - b.order || a.id - b.id);

const nextContentId = (): number =>
  contents.reduce((max, c) => Math.max(max, c.id), 0) + 1;

const nextCategoryId = (): number =>
  categories.reduce((max, c) => Math.max(max, c.id), 0) + 1;

const nextCategoryOrder = (): number =>
  categories.reduce((max, c) => Math.max(max, c.order), 0) + 1;

// Today as YYYY.MM.DD in KST. PRD §4.5.
function todayKst(): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date()).replace(/-/g, '.');
}

// Strip HTML tags and collapse whitespace — used to derive `preview` from the
// first text block (PRD §4.5). Mirrors the prototype's stripHtml.
function stripHtml(html: string): string {
  return (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function derivePreview(blocks: ContentBlock[]): string {
  const text = blocks
    .map((b) => {
      if (b.type === 'text') return stripHtml(b.text);
      if (b.type === 'video') return (b.url ?? '').trim();
      return b.text?.trim() ?? '';
    })
    .filter(Boolean)
    .join(' ');
  return text.length > 100 ? `${text.slice(0, 100)}...` : text;
}

function blockHasContent(b: ContentBlock): boolean {
  if (b.type === 'text') return stripHtml(b.text).length > 0;
  if (b.type === 'image') return Boolean(b.dataUrl);
  if (b.type === 'video') return Boolean((b.url ?? '').trim());
  return false;
}

function toSummary(record: ContentRecord): ContentSummary {
  return {
    eid: toEid(record.id),
    title: record.title,
    categoryId: record.categoryId,
    categoryName: categoryName(record.categoryId),
    thumbnailUrl: record.thumbnailUrl,
    preview: record.preview,
    status: record.status,
    publishedAt: record.publishedAt,
    homeExposed: record.homeExposed,
  };
}

// PRD §3.9 — library-wide ON count, the same number the BE reports.
const countHomeExposed = (): number => contents.filter((c) => c.homeExposed).length;

function toDetail(record: ContentRecord): ContentDetail {
  return { ...toSummary(record), blocks: clone(record.blocks) };
}

function toCategory(record: CategoryRecord): Category {
  return { ...record, contentCount: countByCategory(record.id) };
}

function findContentOrThrow(eid: string): ContentRecord {
  const record = contents.find((c) => c.id === fromEid(eid));
  if (!record) {
    throw new HealthEncyclopediaApiError('NOT_FOUND', '콘텐츠를 찾을 수 없습니다.');
  }
  return record;
}

function findCategoryOrThrow(id: number): CategoryRecord {
  const record = categories.find((c) => c.id === id);
  if (!record) {
    throw new HealthEncyclopediaApiError('NOT_FOUND', '카테고리를 찾을 수 없습니다.');
  }
  return record;
}

// ── Validation (PRD §4.3 / §4.4) ─────────────────────────────────────────────
function validateContentInput(input: CreateContentInput | UpdateContentInput): void {
  const fields: Record<string, string> = {};

  if (input.title !== undefined) {
    const title = input.title.trim();
    if (!title) fields.title = '제목을 입력해주세요.';
    else if (title.length > MAX_TITLE) fields.title = '제목은 100자 이내로 입력해주세요.';
  }
  if (input.categoryId !== undefined && input.categoryId == null) {
    fields.categoryId = '카테고리를 선택해주세요.';
  }
  // Block-editor content + ADM.015/018 limits (backstop mirroring validate-form).
  let blockCode:
    | 'CONTENT_REQUIRED'
    | 'TOO_MANY_BLOCKS'
    | 'TEXT_BLOCK_TOO_LONG'
    | 'CONTENT_URL_NOT_ALLOWED'
    | 'VIDEO_TITLE_INVALID'
    | null = null;
  if (input.blocks !== undefined) {
    // SHCT-662 / PRD 10 v2.13 §3.7 — the server drops unfilled media blocks too, so this mock
    // has to, or it rejects a payload the real BE would accept and the two sides of the wire
    // disagree about what "본문이 비었다" means. §8.1: that copy is for 유효 블록 0개 only.
    const blocks = withoutEmptyMediaBlocks(input.blocks);
    if (!blocks.some(blockHasContent)) {
      fields.blocks = '최소 1개 블록에 내용을 입력해주세요.';
      blockCode = 'CONTENT_REQUIRED';
    } else if (blocks.length > MAX_BLOCKS) {
      fields.blocks = `콘텐츠 블록은 최대 ${MAX_BLOCKS}개까지 추가할 수 있습니다.`;
      blockCode = 'TOO_MANY_BLOCKS';
    } else if (blocks.some((b) => b.type === 'text' && stripHtml(b.text).length > MAX_TEXT_BLOCK)) {
      fields.blocks = `텍스트 블록은 최대 ${MAX_TEXT_BLOCK}자까지 입력할 수 있습니다.`;
      blockCode = 'TEXT_BLOCK_TOO_LONG';
    } else if (
      blocks.some((b) => b.type === 'video' && (b.url ?? '').trim() !== '' && !isVideoUrlValid(b.url ?? ''))
    ) {
      fields.blocks = '허용되지 않은 링크입니다. YouTube 또는 Vimeo 링크만 등록할 수 있습니다.';
      blockCode = 'CONTENT_URL_NOT_ALLOWED';
    } else if (blocks.some((b) => b.type === 'video' && !isVideoTitleValid(b.title))) {
      fields.blocks = `영상 제목은 1~${VIDEO_TITLE_MAX}자로 입력해주세요.`;
      blockCode = 'VIDEO_TITLE_INVALID';
    }
  }

  if (Object.keys(fields).length > 0) {
    // Surface the first code so callers can branch; all messages ride in `fields`.
    const code = fields.title
      ? fields.title.includes('이내')
        ? 'TITLE_TOO_LONG'
        : 'TITLE_REQUIRED'
      : fields.categoryId
        ? 'CATEGORY_REQUIRED'
        : (blockCode ?? 'CONTENT_REQUIRED');
    throw new HealthEncyclopediaApiError(code, '입력값을 확인해주세요.', { fields });
  }
}

function validateCategoryName(name: string | undefined, excludeId?: number): string {
  const cleaned = (name ?? '').trim();
  if (!cleaned) {
    throw new HealthEncyclopediaApiError(
      'CATEGORY_NAME_REQUIRED',
      '카테고리 이름을 입력해주세요.',
      { fields: { name: '카테고리 이름을 입력해주세요.' } },
    );
  }
  if (cleaned.length > MAX_CATEGORY_NAME) {
    throw new HealthEncyclopediaApiError(
      'CATEGORY_NAME_TOO_LONG',
      '카테고리 이름은 20자 이내로 입력해주세요.',
      { fields: { name: '카테고리 이름은 20자 이내로 입력해주세요.' } },
    );
  }
  if (categories.some((c) => c.id !== excludeId && c.name === cleaned)) {
    throw new HealthEncyclopediaApiError(
      'CATEGORY_NAME_DUPLICATE',
      '이미 같은 이름의 카테고리가 있습니다.',
      { fields: { name: '이미 같은 이름의 카테고리가 있습니다.' } },
    );
  }
  return cleaned;
}

// PRD §3.6 — PUBLISHED → DRAFT is forbidden.
function ensureStatusTransition(prev: ContentStatus, next: ContentStatus | undefined): void {
  if (next === 'DRAFT' && prev === 'PUBLISHED') {
    throw new HealthEncyclopediaApiError(
      'STATUS_TRANSITION_FORBIDDEN',
      '게시된 콘텐츠는 임시저장 상태로 되돌릴 수 없습니다.',
    );
  }
}

// ── Server-side list simulation (PRD §3.1–3.3) ───────────────────────────────
function applyFilterSortPage(params: ContentListParams): ContentListResult {
  let rows = [...contents];

  // Category scope. (미분류/UNCATEGORIZED removed from scope — ADM.010.)
  if (typeof params.categoryId === 'number') {
    rows = rows.filter((c) => c.categoryId === params.categoryId);
  }

  // Search: title OR category name, within the current scope (case-insensitive).
  const q = params.q?.trim().toLowerCase();
  if (q) {
    rows = rows.filter((c) => {
      const name = (categoryName(c.categoryId) ?? '').toLowerCase();
      return c.title.toLowerCase().includes(q) || name.includes(q);
    });
  }

  // Sort.
  const dir = params.sortDir === 'asc' ? 1 : -1;
  rows.sort((a, b) => {
    switch (params.sortKey) {
      case 'title':
        return a.title.localeCompare(b.title) * dir;
      case 'category':
        return (categoryName(a.categoryId) ?? '').localeCompare(categoryName(b.categoryId) ?? '') * dir;
      case 'publishedAt':
        return (a.publishedAt ?? '').localeCompare(b.publishedAt ?? '') * dir;
      case 'eid':
      default:
        return (a.id - b.id) * dir;
    }
  });

  const totalElements = rows.length;
  const size = Math.max(1, params.size);
  const totalPages = Math.max(1, Math.ceil(totalElements / size));
  const page = Math.min(Math.max(1, params.page), totalPages);
  const slice = rows.slice((page - 1) * size, page * size);

  return {
    content: slice.map(toSummary),
    page,
    size,
    totalElements,
    totalPages,
    // Deliberately counted over `contents`, not `rows` — the cap is library-wide,
    // so the filter/search/page narrowing above must not affect it.
    homeExposedCount: countHomeExposed(),
    // The real server reads this from one shared settings row; the mock stands in with the
    // same documented default so a mock-backed screen behaves like the live one.
    maxHomeExposed: MAX_HOME_EXPOSED_FALLBACK,
  };
}

// ── Public API ───────────────────────────────────────────────────────────────
export const healthEncyclopediaMockApi: HealthEncyclopediaApi = {
  async listContents(params: ContentListParams): Promise<ContentListResult> {
    await delay();
    return clone(applyFilterSortPage(params));
  },

  async getContent(eid: string): Promise<ContentDetail> {
    await delay();
    return toDetail(findContentOrThrow(eid));
  },

  async createContent(input: CreateContentInput): Promise<ContentDetail> {
    await delay();
    validateContentInput(input);
    const id = nextContentId();
    // SHCT-662 — persist the same filtered array the validation judged.
    const blocks = clone(withoutEmptyMediaBlocks(input.blocks));
    const record: ContentRecord = {
      id,
      title: input.title.trim(),
      categoryId: input.categoryId,
      thumbnailUrl: input.thumbnailUrl ?? null,
      preview: derivePreview(blocks),
      status: input.status,
      publishedAt: input.status === 'PUBLISHED' ? todayKst() : null,
      // PRD §4.2 — 기본 OFF. Home exposure is only ever set from the list toggle.
      homeExposed: false,
      blocks,
    };
    contents.unshift(record);
    return toDetail(record);
  },

  async updateContent(eid: string, input: UpdateContentInput): Promise<ContentDetail> {
    await delay();
    const record = findContentOrThrow(eid);
    validateContentInput(input);
    ensureStatusTransition(record.status, input.status);

    if (input.title !== undefined) record.title = input.title.trim();
    if (input.categoryId !== undefined) record.categoryId = input.categoryId;
    // SHCT-896 — mirror the SERVER's three states exactly, not a friendlier version of them.
    // This mock used to clear on any provided value, so a developer exercising the removal in mock mode
    // saw it work while the real server silently kept the old thumbnail — the one place the defect was
    // cheap to catch reported success. `clearThumbnail` removes; a bare null means "leave unchanged".
    if (input.clearThumbnail) {
      record.thumbnailUrl = null;
    } else if (input.thumbnailUrl != null) {
      record.thumbnailUrl = input.thumbnailUrl;
    }
    if (input.blocks !== undefined) {
      record.blocks = clone(withoutEmptyMediaBlocks(input.blocks));
      record.preview = derivePreview(record.blocks);
    }
    if (input.status !== undefined) {
      const wasPublished = record.status === 'PUBLISHED';
      record.status = input.status;
      if (input.status === 'PUBLISHED' && !wasPublished) record.publishedAt = todayKst();
      // PRD §3.9 조건분기 — 게시중→임시저장 전환 저장 시 homeExposed 자동 해제.
      // A DRAFT content can never be home-exposed, so the slot is freed too.
      if (input.status === 'DRAFT') record.homeExposed = false;
    }
    return toDetail(record);
  },

  async deleteContent(eid: string): Promise<void> {
    await delay();
    const record = findContentOrThrow(eid);
    contents = contents.filter((c) => c.id !== record.id);
  },

  // PRD §3.9 — mirrors the BE guards so mock mode exercises the same failures:
  // only PUBLISHED can turn ON, and the cap is checked only when flipping an
  // OFF content ON (re-confirming an already-ON content never trips it).
  async setHomeExpose(eid: string, homeExposed: boolean): Promise<ContentDetail> {
    await delay();
    const record = findContentOrThrow(eid);
    const turningOn = homeExposed && !record.homeExposed;

    if (turningOn) {
      if (record.status !== 'PUBLISHED') {
        throw new HealthEncyclopediaApiError(
          'HOME_EXPOSE_NOT_PUBLISHED',
          '게시중인 콘텐츠만 홈 노출로 지정할 수 있습니다.',
        );
      }
      if (countHomeExposed() >= MAX_HOME_EXPOSED_FALLBACK) {
        throw new HealthEncyclopediaApiError(
          'HOME_EXPOSE_LIMIT_EXCEEDED',
          `홈 노출은 최대 ${MAX_HOME_EXPOSED_FALLBACK}건까지 선택할 수 있습니다.`,
        );
      }
    }

    record.homeExposed = homeExposed;
    return toDetail(record);
  },

  async listCategories(): Promise<CategoryListResult> {
    await delay();
    return {
      categories: sortedCategories().map(toCategory),
    };
  },

  async createCategory(input: CategoryNameInput): Promise<Category> {
    await delay();
    const name = validateCategoryName(input.name);
    const record: CategoryRecord = { id: nextCategoryId(), name, order: nextCategoryOrder() };
    categories.push(record);
    return toCategory(record);
  },

  async updateCategory(id: number, input: CategoryNameInput): Promise<Category> {
    await delay();
    const record = findCategoryOrThrow(id);
    record.name = validateCategoryName(input.name, id);
    return toCategory(record);
  },

  async deleteCategory(id: number): Promise<void> {
    await delay();
    findCategoryOrThrow(id);
    if (countByCategory(id) > 0) {
      throw new HealthEncyclopediaApiError(
        'CATEGORY_IN_USE',
        '콘텐츠가 있는 카테고리는 삭제할 수 없습니다. 콘텐츠를 다른 카테고리로 이동한 뒤 삭제해주세요.',
      );
    }
    categories = categories.filter((c) => c.id !== id);
  },

  async uploadImage(file: File): Promise<UploadResult> {
    await delay();
    // Clarification ADM.013 — JPG/PNG only, ≤ 5MB.
    const okType = /^image\/(jpeg|png)$/.test(file.type);
    const okSize = file.size <= 5 * 1024 * 1024;
    if (!okType || !okSize) {
      // BE returns CONTENT_FILE_INVALID from the magic-byte sniff.
      throw new HealthEncyclopediaApiError(
        'CONTENT_FILE_INVALID',
        '이미지 업로드에 실패했습니다. 파일 형식 및 크기를 확인해주세요.',
      );
    }
    // Mock: return a base64 data: URL in place of a CDN URL (contract §4).
    const url = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    return { url };
  },
};
